import { useAppForm } from '@/shared/lib/form';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { Show } from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import {
  hasOnlyI4ProEmailChars,
  I4PRO_EMAIL_CHARS_MESSAGE,
  normalizeEmail,
  validateCpf,
  validatePhone,
} from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  RadioCard,
  Select,
} from '@noorden-insurtech/alba-design-system';
import { revalidateLogic } from '@tanstack/react-form';
import { Building, User as UserIcon, Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import type { User } from '../../../services';
import type { UserDto } from '@/shared/types';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import { removeFormatPhone } from '@/shared/utils/remove-format-phone';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import { useIsInsurerAdminOrCx } from '@/shared/hooks';
import {
  getRoleOptionsByUserType,
  getUserGroup,
  getUserTypeAndRoleFromGroup,
  UserRole,
} from '@/shared/utils/get-group';
import { useAuth } from '@/app/context';
import {
  EmailAlreadyInUseError,
  InvalidEmailError,
  useUpdateUser,
} from '../hooks/use-update-user.hook';
import { useUser } from '../hooks/use-user.hook';
import { UserTypeRadioItem } from './user-type-radio-items.component';

type EditUserModalProps = {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Lista global (`User`) ou lista por corretora (`UserDto`). */
  initialUser?: User | UserDto | null;
};

const schema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório'),
  cpf: z.string().refine(v => validateCpf(v), 'CPF inválido'),
  email: z.email('E-mail inválido'),
  phone: z.string().refine(v => validatePhone(v), 'Telefone inválido'),
  role: z.string().min(1, 'Perfil é obrigatório'),
  partyId: z.string(),
});

type EditFormValues = {
  name: string;
  cpf: string;
  email: string;
  phone: string;
  role: string;
  partyId: string;
};

export function EditUserModal({
  userId,
  isOpen,
  onClose,
  onSuccess,
  initialUser,
}: EditUserModalProps) {
  const { user: authUser } = useAuth();
  const { data: user, isFetching } = useUser(userId || undefined);
  const { mutateAsync, isPending } = useUpdateUser();
  const { isInsurerAdminOrCx } = useIsInsurerAdminOrCx();

  const sourceUser: any = user ?? initialUser ?? undefined;
  const isEditingSelf =
    Boolean(sourceUser?.identityId && authUser?.userId) &&
    sourceUser.identityId === authUser?.userId;
  const parsed = getUserTypeAndRoleFromGroup(sourceUser?.groupName || '');
  const userType =
    parsed?.userType ??
    (sourceUser?.groupName?.toLowerCase().includes('broker')
      ? 'broker'
      : sourceUser?.groupName?.toLowerCase().includes('advisory')
        ? 'advisory'
        : 'insurer');
  const roleOptions = useMemo(
    () => getRoleOptionsByUserType(userType),
    [userType]
  );
  const initialPartyValue: string =
    sourceUser?.partyCode ||
    sourceUser?.advisoryId ||
    sourceUser?.partyId ||
    '';
  // Trocar a corretora é só da Alba (Seg Adm/CX); o master edita o resto.
  const canChangeBroker = isInsurerAdminOrCx === true && userType === 'broker';
  const isBusy = isPending || isFetching;

  const { AppField, handleSubmit, reset, setFieldValue } = useAppForm({
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
      role: '',
      partyId: '',
    },
    onSubmit: async () => {},
    validationLogic: revalidateLogic(),
    validators: {
      onChange: schema,
      onSubmitAsync: async ({ value }) => {
        const { role, email, name, phone, cpf, partyId } =
          value as EditFormValues;

        const identityId = sourceUser?.identityId || sourceUser?.id;
        if (!identityId) {
          return null;
        }

        // Mesma regra da Api: só barra o e-mail NOVO, para quem já tem `+` no
        // atual continuar editável.
        const emailChanged =
          email.trim().toLowerCase() !==
          String(sourceUser?.email ?? '')
            .trim()
            .toLowerCase();
        if (emailChanged && !hasOnlyI4ProEmailChars(email)) {
          return {
            fields: { email: { message: I4PRO_EMAIL_CHARS_MESSAGE } },
          } as const;
        }

        // Usuário não pode alterar seu próprio grupo - não envia custom:group
        const group = isEditingSelf
          ? undefined
          : getUserGroup(userType, role as UserRole);
        const nextPartyId = partyId.trim();
        const payload = {
          email: normalizeEmail(email),
          name: name.trim(),
          ...(canChangeBroker &&
            nextPartyId &&
            nextPartyId !== initialPartyValue && { partyId: nextPartyId }),
          customAttributes: {
            ...(group && { 'custom:group': group }),
            'custom:contact_number':
              removeFormatPhone(phone || sourceUser?.phone) || '',
            'custom:register_number': removeFormatRegisterNumber(cpf) || '',
            ...(group && { 'custom:profile': getRoleLabel(group) }),
          },
          identityId,
          recordId: sourceUser?.RecordId || '',
          recordUsage: sourceUser?.RecordUsage || '',
        };

        try {
          await mutateAsync(payload);
          toaster.create({
            type: 'success',
            title: 'Usuário atualizado com sucesso',
            description: 'As informações do usuário foram salvas.',
          });
          reset();
          onSuccess?.();
          return null;
        } catch (error) {
          if (
            error instanceof EmailAlreadyInUseError ||
            error instanceof InvalidEmailError
          ) {
            return { fields: { email: { message: error.message } } } as const;
          }
          const msg = (
            error instanceof Error ? error.message : 'Erro ao atualizar usuário'
          ).trim();
          toaster.create({
            type: 'error',
            title: 'Erro ao atualizar usuário',
            description: msg,
          });
          return null;
        }
      },
    },
  });

  useEffect(() => {
    if (!sourceUser) return;
    const parsed = getUserTypeAndRoleFromGroup(sourceUser.groupName || '');
    const initialRole = parsed?.role ?? sourceUser.role ?? '';
    setFieldValue('name', sourceUser.name || '');
    setFieldValue('email', normalizeEmail(sourceUser.email));
    setFieldValue('role', initialRole);
    setFieldValue('phone', sourceUser.phone || '');
    setFieldValue('cpf', sourceUser.registerNumber || '');
    const partyValue =
      sourceUser.partyCode || sourceUser.advisoryId || sourceUser.partyId || '';
    setFieldValue('partyId', partyValue);
  }, [sourceUser, setFieldValue]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[calc(100vw-1.5rem)] max-w-3xl max-h-[calc(100vh-1.5rem)] overflow-hidden'>
          <Dialog.Header>
            <Dialog.Title>Editar Usuário</Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>
          <Dialog.Body className='overflow-y-auto px-4 sm:px-6'>
            <div className='flex flex-col gap-3 mb-4'>
              <RadioCard.Root
                value={userType}
                disabled
                className='pointer-events-none opacity-90'
              >
                <RadioCard.Label>Tipo de Usuário</RadioCard.Label>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                  <UserTypeRadioItem
                    value='insurer'
                    icon={Building}
                    label='Seguradora'
                    description='Usuários internos da seguradora'
                  />
                  <UserTypeRadioItem
                    value='advisory'
                    icon={Users}
                    label='Assessoria'
                    description='Usuários de assessoria parceira'
                  />
                  <UserTypeRadioItem
                    value='broker'
                    icon={UserIcon}
                    label='Corretor'
                    description='Usuários de corretor parceiro'
                  />
                </div>
              </RadioCard.Root>
            </div>

            <form
              onSubmit={e => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
              className='grid grid-cols-1 gap-4 md:grid-cols-2'
            >
              <Show when={userType === 'advisory' || userType === 'broker'}>
                <div className='md:col-span-2'>
                  <AppField name='partyId'>
                    {field => (
                      <field.AdvisoryComboboxField
                        label={
                          userType === 'advisory'
                            ? 'Assessoria Vinculada *'
                            : 'Corretora Vinculada *'
                        }
                        required
                        placeholder={
                          userType === 'advisory'
                            ? 'Selecione a assessoria'
                            : 'Selecione a corretora'
                        }
                        disabled={!canChangeBroker || isBusy}
                        helperText={
                          canChangeBroker
                            ? 'O usuário deixa de ver as cotações e apólices da corretora atual.'
                            : undefined
                        }
                        partyType={userType as 'advisory' | 'broker'}
                      />
                    )}
                  </AppField>
                </div>
              </Show>

              <div className='md:col-span-2'>
                <AppField name='name'>
                  {field => (
                    <field.InputField
                      label='Nome *'
                      helperText=''
                      type='text'
                      placeholder='Nome'
                      required
                      maxLength={FIELD_MAX_LENGTHS.personName}
                      disabled={isBusy}
                    />
                  )}
                </AppField>
              </div>

              <div className='md:col-span-2'>
                <AppField name='email'>
                  {field => (
                    <field.InputField
                      label='E-mail *'
                      helperText='É o login do usuário: ao alterar, ele passa a entrar com o novo e-mail.'
                      type='email'
                      lowercase
                      placeholder='E-mail'
                      required
                      maxLength={FIELD_MAX_LENGTHS.email}
                      disabled={isBusy}
                    />
                  )}
                </AppField>
              </div>

              <div className='md:col-span-2'>
                <AppField name='cpf'>
                  {field => (
                    <field.InputField
                      label='CPF *'
                      helperText=''
                      type='text'
                      placeholder='XXX.XXX.XXX-XX'
                      maxLength={FIELD_MAX_LENGTHS.cpf}
                      mask='cpf'
                      required
                      disabled={isBusy}
                    />
                  )}
                </AppField>
              </div>

              <div className='grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2'>
                <AppField name='role'>
                  {field => (
                    <Field.Root
                      required
                      invalid={
                        !field.state.meta.isValid &&
                        (field.state.meta.isTouched ||
                          field.form.state.isSubmitted)
                      }
                    >
                      <Field.Label className='text-alba-black-500'>
                        Perfil*
                        {isEditingSelf && (
                          <span className='ml-1 text-alba-gray-500 text-xs font-normal'>
                            (não é possível alterar seu próprio perfil)
                          </span>
                        )}
                      </Field.Label>
                      <Select.Root
                        items={roleOptions}
                        value={field.state.value ? [field.state.value] : []}
                        onValueChange={details =>
                          field.handleChange(details.value[0] || '')
                        }
                        positioning={{ sameWidth: true }}
                        disabled={isBusy || isEditingSelf}
                      >
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder='Selecione o perfil' />
                            <Select.Indicator />
                          </Select.Trigger>
                        </Select.Control>
                        <Select.Portal>
                          <Select.Positioner>
                            <Select.Content>
                              {roleOptions.map(option => (
                                <Select.Item key={option.value} item={option}>
                                  <Select.ItemText>
                                    {option.label}
                                  </Select.ItemText>
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Portal>
                      </Select.Root>
                    </Field.Root>
                  )}
                </AppField>

                <AppField name='phone'>
                  {field => (
                    <field.InputField
                      label='Telefone'
                      helperText=''
                      type='tel'
                      mask='(99) 9{8,9}'
                      placeholder='(XX) XXXXXXXXX'
                      maxLength={FIELD_MAX_LENGTHS.phone}
                      required={false}
                      disabled={isBusy}
                    />
                  )}
                </AppField>
              </div>
            </form>
          </Dialog.Body>
          <Dialog.Footer className='flex flex-col-reverse gap-2 px-4 pb-4 sm:flex-row sm:justify-end sm:px-6'>
            <Button
              variant='ghost'
              className='w-full sm:w-auto'
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </Button>
            <Button
              loadingText='Salvando...'
              loading={isPending}
              variant='primary'
              className='w-full sm:w-auto'
              onClick={() => handleSubmit()}
            >
              Salvar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
